import * as actions from './slice'
import { stackAlert } from '../toast/helpers'

/**
 * Event-bus definitions for the notification centre.
 *
 * These mirror `paid-consumer-homepage/event-bus.js`: `onStart` flips the
 * loading flag, `onSuccess` writes the payload and clears it, `onError` records
 * the error and raises a toast.
 *
 * The event names below are the contract we expect from the backend and do not
 * exist yet — see uxspecs/notifications/notification-center-requirements.md §6.
 * Until they do, `useNotifications` drives the same actions from mock data, so
 * the reducer/selector path being exercised is already the real one.
 *
 * TODO(API) — everything the backend still owes this feature:
 * - `notificationsGet { from, size }` must return `{ items, total, unreadCount }`.
 *   The client already sends `from`/`size` and pages on it (NC-31): `total`
 *   drives "Load more", and `unreadCount` is the count across the whole
 *   history, not the loaded page, or the badge undercounts (NC-50). Each item
 *   is `{ id, type, title, body, createdAt, read, action }` — field-by-field
 *   notes live in components/notification-center/mock-notifications.js.
 * - `notificationsMarkRead { id }` / `notificationsMarkAllRead` —
 *   fire-and-forget; the reducer is already optimistic and a failure triggers
 *   a re-fetch rather than a guessed rollback (see `setNeedsResync` below).
 * - `notificationsDismiss { id }` — only if the hi-fi per-row ✕ lands (§5);
 *   the `dismiss` reducer already exists for it.
 * - `notificationEvent { eventType, data }` — the analytics sink, same shape as
 *   `homeEnterpriseEvent`; `useTrackNotificationEvent` holds its triggers back
 *   until NOTIFICATIONS_API_ENABLED so nothing is fired into the void.
 * - Preferences (NC-40) need no new event: they ride the existing
 *   `updateProfile { partial }`, as `profile.notificationPreferences`
 *   (`{ [NOTIFICATION_TYPE]: boolean }`), next to `profile.newsletter`. What the
 *   backend does owe is honouring them when it decides what to send — the
 *   client never filters a payload it was given.
 * - Live updates: unread count pushed over the existing websocket event bus so
 *   counts stay correct across tabs without polling (NC-50).
 * - Gating: the API must never send notifications for features the tenant or
 *   user can't access (NC-52) — the client renders whatever it is given.
 */

// No `onStart` here, unlike the sibling slices: one event serves both the first
// page and "Load more", and only the caller knows which — `useNotifications`
// dispatches `setLoading` or `setLoadingMore` before triggering. `setNotifications`
// and `setError` then read the slice's `appending` flag to decide whether the
// payload replaces the list or extends it.
export const getNotifications = {
  eventName: 'notificationsGet',
  onSuccess: {
    action: actions.setNotifications,
    redux: true
  },
  onError: [
    { action: actions.setError, redux: true },
    { action: stackAlert('Unable to load notifications', 'error'), redux: true }
  ]
}

// Read/dismiss are optimistic: the reducer has already updated by the time
// these fire. A failure can't be rolled back reliably (the user may have read
// more rows since), so it raises `needsResync` and `useNotifications` re-fetches
// — the list is then whatever the server actually has.
export const markNotificationRead = {
  eventName: 'notificationsMarkRead',
  onError: [
    { action: actions.setNeedsResync, redux: true },
    { action: stackAlert('Unable to update that notification', 'error'), redux: true }
  ]
}

export const markAllNotificationsRead = {
  eventName: 'notificationsMarkAllRead',
  onError: [
    { action: actions.setNeedsResync, redux: true },
    { action: stackAlert('Unable to mark notifications as read', 'error'), redux: true }
  ]
}
